import type {
  DragEndEvent,
  DragMoveEvent,
  PointerSensorOptions,
  TouchSensorOptions,
} from '@dnd-kit/core'
import {
  PointerSensor,
  TouchSensor,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core'
import type { PointerEvent as ReactPointerEvent, TouchEvent as ReactTouchEvent } from 'react'

import type { TripStop } from 'tripPlanner/api/types'
import type { TripListMoveDayOption, TripListMovePlacement } from './tripListMoveDayOptions'
import type { TripListGroup, TripListRow } from './tripListTypes'
import { getTripListRowTripItemId } from './tripListCartPrices'

// Keep a 1px buffer so normal clicks still work, while drag pickup is near-instant.
export const POINTER_DRAG_ACTIVATION_DISTANCE = 1
export const DROP_ANIMATION_DURATION = 150
export const TOUCH_DRAG_ACTIVATION_DELAY = 250
export const TOUCH_DRAG_ACTIVATION_TOLERANCE = 5
const TRIP_LIST_DRAG_DISABLED_SELECTOR = [
  '[data-trip-list-drag-disabled="true"]',
  'a',
  'button',
  'input',
  'select',
  'textarea',
  '[contenteditable="true"]',
].join(',')

export const dropAnimationSideEffects = defaultDropAnimationSideEffects({
  className: {
    dragOverlay: 'dropped',
  },
  styles: {
    active: {
      visibility: 'hidden',
    },
  },
})

export const autoScrollConfig = {
  threshold: {
    x: 0,
    y: 0.2,
  },
}

export type DropTargetPlacement = 'before' | 'after'
export type PointerCoordinates = { x: number; y: number }
interface RowDropTarget {
  type: 'row'
  rowId: string
  placement: DropTargetPlacement
}

interface GroupDropTarget {
  type: 'group'
  groupId: string
  placement: DropTargetPlacement
}

export type DropTarget = RowDropTarget | GroupDropTarget

function isTripListDragEventTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !target.closest(TRIP_LIST_DRAG_DISABLED_SELECTOR)
}

export class TripListPointerSensor extends PointerSensor {
  static activators = [{
    eventName: 'onPointerDown' as const,
    handler: (event: ReactPointerEvent, options: PointerSensorOptions): boolean => {
      if (!isTripListDragEventTarget(event.nativeEvent.target)) return false
      return PointerSensor.activators[0].handler(event, options)
    },
  }]
}

export class TripListTouchSensor extends TouchSensor {
  static activators = [{
    eventName: 'onTouchStart' as const,
    handler: (event: ReactTouchEvent, options: TouchSensorOptions): boolean => {
      if (!isTripListDragEventTarget(event.nativeEvent.target)) return false
      return TouchSensor.activators[0].handler(event, options)
    },
  }]
}

export function moveItemToDropTarget<T>(items: Array<T>, item: T, targetItem: T, placement: DropTargetPlacement): Array<T> {
  if (item === targetItem) return items

  const nextItems = items.filter(currentItem => currentItem !== item)
  const targetIndex = nextItems.indexOf(targetItem)
  if (targetIndex === -1) return items
  nextItems.splice(placement === 'after' ? targetIndex + 1 : targetIndex, 0, item)
  return nextItems
}

export function moveItemToGroupTarget<T>(items: Array<T>, item: T, targetItems: Array<T>, placement: DropTargetPlacement): Array<T> {
  const nextItems = items.filter(currentItem => currentItem !== item)
  const targetIndexes = targetItems
    .map(targetItem => nextItems.indexOf(targetItem))
    .filter(index => index !== -1)
  if (targetIndexes.length === 0) return [...nextItems, item]

  const insertionIndex = placement === 'before' ? Math.min(...targetIndexes) : Math.max(...targetIndexes) + 1
  nextItems.splice(insertionIndex, 0, item)
  return nextItems
}

export function getDayIdFromGroupId(groupId: string): number | undefined {
  const match = /^(?:empty-)?day:(\d+)$/.exec(groupId)
  if (!match) return undefined

  const dayId = Number(match[1])
  return Number.isFinite(dayId) ? dayId : undefined
}

// Only the first renderable group per stop carries a stopHeader, so fall back to
// the group's own rows to resolve the stop for later day groups in the same stop.
function getTripListGroupStop(group: TripListRenderableGroup): TripStop | undefined {
  return group.group.stopHeader?.stop ?? group.rows[0]?.row.stop
}

// day:/empty-day: groups encode their day in the id; unplanned:/empty-stop: groups
// have no day. Existing rows already carry the resolved day for populated groups.
function getTripListGroupDay(group: TripListRenderableGroup, stop: TripStop | undefined) {
  const dayId = getDayIdFromGroupId(group.id)
  if (dayId !== undefined) return stop?.days.find(day => day.id === dayId)
  return group.rows[0]?.row.day
}

export function getDropPlacementForGroupTarget(placement: DropTargetPlacement): TripListMovePlacement {
  return placement === 'before' ? { placementType: 'start' } : { placementType: 'end' }
}

export function getGroupMoveDayOption(
  group: TripListRenderableGroup,
  placement: DropTargetPlacement,
): TripListMoveDayOption | undefined {
  const stop = getTripListGroupStop(group)
  const day = getTripListGroupDay(group, stop)
  const id = day ? `day:${day.id}` : 'unplanned'

  return {
    id,
    title: group.group.title,
    subtitle: group.group.stopHeader?.title ?? group.group.subtitle ?? group.group.title,
    selected: false,
    target: day ? { targetType: 'day', dayId: day.id } : { targetType: 'unplanned' },
    placement: getDropPlacementForGroupTarget(placement),
  }
}

export function isTripListDropTargetAvailable(
  target: DropTarget,
  _activeRow: TripListRow,
  rowById: ReadonlyMap<string, TripListRow>,
  groupById: ReadonlyMap<string, TripListRenderableGroup>,
): boolean {
  return target.type === 'row' ? rowById.has(target.rowId) : groupById.has(target.groupId)
}

export function getDropPlacementForRowTarget(
  activeRowId: string,
  target: RowDropTarget,
  overRow: TripListRow,
): TripListMovePlacement | undefined {
  if (activeRowId === target.rowId) return undefined

  // Anchor relative to the target row's trip item id (transport rows expose it via
  // `option.itemId`, matching getTripListRowTripItemId) so the persisted placement
  // matches the before/after indicator shown to the user for every row type.
  return {
    placementType: target.placement,
    itemId: getTripListRowTripItemId(overRow),
  }
}

function getTripListDropTargetFromElement(
  element: Element,
  coordinates: PointerCoordinates,
  ignoredRowId: string | undefined,
): DropTarget | undefined {
  const rowElement = element.closest('[data-trip-list-row-id]')
  const rowId = rowElement?.getAttribute('data-trip-list-row-id')
  if (rowElement && rowId && rowId !== ignoredRowId) {
    const rowRect = rowElement.getBoundingClientRect()
    return {
      type: 'row',
      rowId,
      placement: coordinates.y < rowRect.top + rowRect.height / 2 ? 'before' : 'after',
    }
  }

  const groupElement = element.closest('[data-trip-list-group-target-id]')
  const groupId = groupElement?.getAttribute('data-trip-list-group-target-id')
  const placement = groupElement?.getAttribute('data-trip-list-group-target-placement')
  if (groupId && (placement === 'before' || placement === 'after')) {
    return {
      type: 'group',
      groupId,
      placement,
    }
  }

  return undefined
}

export interface TripListRenderableRow {
  row: TripListRow
  showGroupMetadata: boolean
  firstRow: boolean
  lastRow: boolean
  selected: boolean
  isBookingPending: boolean
  isNoteUpdatePending: boolean
  isStatusUpdatePending: boolean
}

export interface TripListRenderableGroup {
  id: string
  group: TripListGroup
  rows: Array<TripListRenderableRow>
}

export function shouldShowTripListRowMetadata(group: TripListGroup, rowIndex: number): boolean {
  return group.rowMetadataDisplay === 'all' || rowIndex === 0
}

export function buildTripListRenderableGroups(
  groups: Array<TripListGroup>,
  selectedRowIds: Set<string>,
  bookingRowId: string | undefined,
  noteUpdateRowId: string | undefined,
  statusUpdateRowId: string | undefined,
): Array<TripListRenderableGroup> {
  const firstGroupId = groups[0]?.id
  const lastGroupId = groups[groups.length - 1]?.id

  return groups.map(group => ({
    id: group.id,
    group,
    rows: group.rows.map((row, rowIndex) => ({
      row,
      showGroupMetadata: shouldShowTripListRowMetadata(group, rowIndex),
      firstRow: firstGroupId === group.id && rowIndex === 0 && !group.stopHeader,
      lastRow: lastGroupId === group.id && rowIndex === group.rows.length - 1,
      selected: selectedRowIds.has(row.id),
      isBookingPending: bookingRowId === row.id,
      isNoteUpdatePending: noteUpdateRowId === row.id,
      isStatusUpdatePending: statusUpdateRowId === row.id,
    })),
  }))
}

export function buildOrderedTripListRenderableGroups(
  renderableGroups: Array<TripListRenderableGroup>,
  rowOrderIndexById: Map<string, number>,
): Array<TripListRenderableGroup> {
  const firstGroupId = renderableGroups[0]?.id
  const lastGroupId = renderableGroups[renderableGroups.length - 1]?.id

  return renderableGroups.map(group => ({
    ...group,
    rows: [...group.rows]
      .sort((firstRow, secondRow) => {
        return (rowOrderIndexById.get(firstRow.row.id) ?? 0) - (rowOrderIndexById.get(secondRow.row.id) ?? 0)
      })
      .map((row, rowIndex, rows) => ({
        ...row,
        showGroupMetadata: shouldShowTripListRowMetadata(group.group, rowIndex),
        firstRow: firstGroupId === group.id && rowIndex === 0 && !group.group.stopHeader,
        lastRow: lastGroupId === group.id && rowIndex === rows.length - 1,
      })),
  }))
}

export function buildOptimisticTripListRenderableGroups(
  renderableGroups: Array<TripListRenderableGroup>,
  rowGroupIdOverrideById: Map<string, string>,
): Array<TripListRenderableGroup> {
  if (rowGroupIdOverrideById.size === 0) return renderableGroups

  const rowById = new Map(renderableGroups.flatMap(group => group.rows.map(row => [row.row.id, row] as const)))

  return renderableGroups.map(group => {
    const targetStop = getTripListGroupStop(group)
    const day = getTripListGroupDay(group, targetStop)
    const movedRows = Array.from(rowGroupIdOverrideById.entries())
      .filter(([, groupId]) => groupId === group.id)
      .map(([rowId]) => rowById.get(rowId))
      .filter(row => row !== undefined)
      .map(row => ({
        ...row,
        row: {
          ...row.row,
          day,
          dayLabel: group.group.title,
          stop: targetStop ?? row.row.stop,
        },
      }))
    const rows = [
      ...group.rows.filter(row => rowGroupIdOverrideById.get(row.row.id) === undefined),
      ...movedRows,
    ]

    return {
      ...group,
      group: {
        ...group.group,
        showEmptyPlaceholder: rows.length === 0 ? group.group.showEmptyPlaceholder : false,
      },
      rows,
    }
  }).filter(group => group.rows.length > 0 || group.group.showEmptyPlaceholder)
}

function getPointerCoordinates(event: Event): PointerCoordinates | undefined {
  if ('clientX' in event && 'clientY' in event) {
    return {
      x: Number(event.clientX),
      y: Number(event.clientY),
    }
  }

  if ('touches' in event) {
    const touches = (event as TouchEvent).touches
    if (touches.length === 0) return undefined

    const touch = touches[0]
    return { x: touch.clientX, y: touch.clientY }
  }

  return undefined
}

export { getPointerCoordinates }

export function getTripListDropTargetFromPoint(
  coordinates: PointerCoordinates,
  ignoredRowId: string | undefined,
): DropTarget | undefined {
  for (const element of document.elementsFromPoint(coordinates.x, coordinates.y)) {
    const target = getTripListDropTargetFromElement(element, coordinates, ignoredRowId)
    if (target) return target
  }

  return undefined
}

export function getDropPointFromDragEvent(
  event: DragMoveEvent | DragEndEvent,
  origin: PointerCoordinates | undefined,
  centerOffset: PointerCoordinates | undefined,
): PointerCoordinates | undefined {
  const translatedRect = event.active.rect.current.translated
  if (translatedRect) {
    return {
      x: translatedRect.left + translatedRect.width / 2,
      y: translatedRect.top + translatedRect.height / 2,
    }
  }

  if (!origin) return undefined

  return {
    x: origin.x + event.delta.x + (centerOffset?.x ?? 0),
    y: origin.y + event.delta.y + (centerOffset?.y ?? 0),
  }
}

export function isSameDropTarget(first: DropTarget | undefined, second: DropTarget | undefined): boolean {
  if (!first || !second) return first === second
  if (first.type !== second.type || first.placement !== second.placement) return false
  if (first.type === 'row' && second.type === 'row') return first.rowId === second.rowId
  if (first.type === 'group' && second.type === 'group') return first.groupId === second.groupId
  return false
}
